import React from 'react';
import { ArrowRight, Globe, Layers, Zap, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { heroContent } from '../../data/mock';

const iconMap = {
  'Global Delivery': Globe,
  'End-to-End Expertise': Layers,
  'Agile Teams': Zap,
  'Security First': Shield
};

export const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-orange-50/30">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-[#f26522]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/4 w-96 h-96 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(30, 58, 95, 0.08) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1e3a5f]/5 border border-[#1e3a5f]/10 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#f26522] animate-pulse" />
            <span className="text-sm font-medium text-[#1e3a5f]">India-Based • Global Delivery</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1e3a5f] leading-tight mb-6 tracking-tight">
            Transform Your Vision into{' '}
            <span className="text-[#f26522]">Scalable Digital Solutions</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto">
            {heroContent.subheadline}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-[#f26522] hover:bg-[#d95a1b] text-white px-8 py-6 text-lg rounded-xl font-medium transition-all hover:shadow-xl hover:shadow-[#f26522]/20 hover:-translate-y-1 group"
            >
              {heroContent.primaryCTA}
              <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              variant="outline"
              onClick={() => scrollToSection('#services')}
              className="border-2 border-[#1e3a5f]/20 text-[#1e3a5f] px-8 py-6 text-lg rounded-xl font-medium hover:bg-[#1e3a5f]/5 hover:border-[#1e3a5f]/30 transition-all"
            >
              {heroContent.secondaryCTA}
            </Button>
          </div>

          {/* Value Props */}
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {heroContent.valueProps.map((prop, i) => {
              const Icon = iconMap[prop.title] || Globe;
              return (
                <div key={i} className="text-center p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-gray-100 hover:shadow-lg hover:border-[#f26522]/20 transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-[#1e3a5f] flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">{prop.title}</h3>
                  <p className="text-sm text-gray-500">{prop.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 rounded-full border-2 border-[#1e3a5f]/30 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-[#f26522] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};
